import useAuthStore from '@/domains/authentication/store/auth.store';
import useSettingsStore from '@/domains/settings/store/settings.store';
import use3DStore from '@/domains/common/store/3d.store';
import useScanStore from '@/domains/scan/store/scan.store';
import useAdminUserStore from '@/domains/admin/store/adminUser.store';
import useAdminOfficeStore from '@/domains/admin/store/adminOffice.store';
import useAdminAnnouncementStore from '@/domains/admin/store/adminAnnouncement.store';
import useOrderStore from '@/domains/order/store/order.store';
import useSoleStore from '@/domains/sole/store/sole.store';
import useSolePresetStore from '@/domains/solePreset/store/solePreset.store';
import usePatientStore from '@/domains/patient/store/patient.store';
import useViewerStore from '@/domains/viewer/store/viewer.store';
import useOfficeStore from '@/domains/office/store/office.store';
import useAdminCatalogStore from '@/domains/admin/store/adminCatalog.store';
import useNavigationStore from '@/domains/common/store/navigation.store';
import StoreEnum from '@/domains/common/typescript/StoreEnum';
import { type Pinia, type Store, getActivePinia } from 'pinia';

interface ExtendedPinia extends Pinia {
    _s: Map<string, Store>;
}

export default function useResetStore() {
    const pinia = getActivePinia() as ExtendedPinia;

    const authStore = useAuthStore();
    const threeDStore = use3DStore();
    const scanStore = useScanStore();
    const settingsStore = useSettingsStore();
    const adminOfficeStore = useAdminOfficeStore();
    const adminUserStore = useAdminUserStore();
    const adminAnnouncementStore = useAdminAnnouncementStore();
    const orderStore = useOrderStore();
    const patientStore = usePatientStore();
    const soleStore = useSoleStore();
    const solePresetStore = useSolePresetStore();
    const viewerStore = useViewerStore();
    const officeStore = useOfficeStore();
    const adminCatalogStore = useAdminCatalogStore();
    const navigationStore = useNavigationStore();

    if (!pinia) {
        throw new Error('There is no active Pinia instance');
    }

    function resetAllStores() {
        // Health data of the last patient lived on until ScanView unmounted, so it survived a
        // logout: on a shared workstation the next practitioner inherited it. Every store the
        // application registers belongs here, hence the guard test on the Pinia registry size.
        // Only exception: the pagination store, a non-sensitive page size kept by the browser.
        const stores = [
            authStore,
            threeDStore,
            scanStore,
            settingsStore,
            adminUserStore,
            adminOfficeStore,
            adminAnnouncementStore,
            adminCatalogStore,
            orderStore,
            patientStore,
            officeStore,
            navigationStore,
            soleStore,
            solePresetStore,
            viewerStore,
        ];

        stores.forEach((store) => {
            try {
                store.$reset();
            } catch (error) {
                // A single failing $reset() must leave neither the following stores unpurged nor
                // the logout unfinished: a denied localStorage is enough to throw here.
                console.error(`Failed to reset the ${store.$id} store`, error);
            }
        });
    }

    // Required on purpose: an empty call used to mean "purge everything", one keystroke away from
    // a targeted reset. Callers that want everything say so with resetAllStores().
    function resetStores(stores: StoreEnum[]): void {
        stores.forEach((store) => {
            switch (store) {
                case StoreEnum.AUTH:
                    authStore.$reset();
                    break;
                case StoreEnum.THREE_D:
                    threeDStore.$reset();
                    break;
                case StoreEnum.SCAN:
                    scanStore.$reset();
                    break;
                case StoreEnum.SETTINGS:
                    settingsStore.$reset();
                    break;
                case StoreEnum.ADMIN:
                    adminUserStore.$reset();
                    adminOfficeStore.$reset();
                    adminAnnouncementStore.$reset();
                    break;
                case StoreEnum.ORDER:
                    orderStore.$reset();
                    break;
                case StoreEnum.PATIENT:
                    patientStore.$reset();
                    break;
                case StoreEnum.SOLE:
                    soleStore.$reset();
                    break;
                case StoreEnum.SOLE_PRESET:
                    solePresetStore.$reset();
                    break;
                case StoreEnum.VIEWER:
                    viewerStore.$reset();
                    break;
                default:
                    break;
            }
        });
    }

    return { resetStores, resetAllStores };
}
